import {Injectable} from '@angular/core';
import {FreeTrialAccount} from '../models/free-trial.model';
import {environment} from '../../../environments/environment';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {LoginPayLoad, NotificationRes} from '../models/account.model';
import {Observable, tap} from 'rxjs';
import {CallScenario, LLM_function, SupportedLanguagesEnum, VoiceBotType} from '../models/voice-bot.model';
import {Message} from '../models/feedback.model';

@Injectable({
    providedIn: 'root'
})
export class DataService {
    private readonly ENDPOINTS = {
        FREE_TRIAL_URL: () => `${environment.freeTrial}`,
        FREE_LOGIN_URL: () => `${environment.login}`,
        CHECK_TOKEN_URL: () => `${environment.authApi}/oauth/check_token`,
        WEBSITE_CRAWLER: () => `${environment.crawlerAPI}/voicebot/crawl_and_summarize`,
        CALL_SCENARIO_URL: () => `${environment.crawlerAPI}/voicebot/generate_tool_access`,
        DELETE_CALL_SCENARIO: (psId: string, toolId: number) => `${environment.phoneServiceAPI}/voicebot/vapi/${psId}/tools/${toolId}`,
        VOICES_URL: () => `${environment.phoneServiceAPI}/voicebot/vapi/voices`,
        PUT_PHONESERVICE_URL: (psId: string) => `${environment.phoneServiceAPI}/phoneservices/${psId}`,
        VOICE_BOT_TYPE_URL: (voiceId: string, psId: string) => `${environment.phoneServiceAPI}/voicebot/vapi/${psId}/voices?voiceId=${voiceId}`,
        TEST_CALL_URL: (psId: string) => `${environment.phoneServiceAPI}/voicebot/vapi/${psId}/calls`,
        NOTIFICATION_URL: (psId: string) => `${environment.phoneServiceAPI}/phoneservices/${psId}/notifications`,
        DELETE_NOTIFICATION_URL: (psId: string, notId: number) => `${environment.phoneServiceAPI}/phoneservices/${psId}/notifications/${notId}`,
        CONTACT_REQUEST_URL: (uaId: number) => `${environment.customerSupportAPI}/useraccounts/${uaId}/messages`
    };
    private token = '';


    constructor(private http: HttpClient) {
    }

    public requestFreeTrial(data: FreeTrialAccount) {
        let headers = new HttpHeaders({
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        });

        headers = this.addInternalHeader(headers);

        return this.http.post<any>(this.ENDPOINTS.FREE_TRIAL_URL(), data, {
            headers: headers
        });
    }

    public login(data: LoginPayLoad) {
        let headers = new HttpHeaders({
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        });

        headers = this.addInternalHeader(headers);

        return this.http.post<any>(this.ENDPOINTS.FREE_LOGIN_URL(), data, {
            headers: headers
        }).pipe(
            tap((res) => {
                this.token = res.access_token;
            })
        );
    }
    
    public checkToken() {
        return this.http.post<any>(this.ENDPOINTS.CHECK_TOKEN_URL(), `token=${this.token}&internal`, {
            headers: {
                'Accept': 'application/json',
                'correlation-id': this.generateUUID(),
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        });
    }
    
    crawlWebsite(website: string, email: string) {
        const data = {
            url: website,
            phoneservice_id: null,
            user_name: email
        };
        return this.http.post<any>(this.ENDPOINTS.WEBSITE_CRAWLER(), data, {
            headers: {
                'content-type': 'application/json'
            }
        });
    }

    fetchVoiceBoxVoices(shortLanguage: SupportedLanguagesEnum) {
        return this.http.get<VoiceBotType[]>(this.ENDPOINTS.VOICES_URL(), {
            headers: {
                'correlation-id': this.generateUUID(),
                'accept': '*/*'
            }
        });
    }

    saveGreeting(greeting: string, phoneserviceId: string) {
        const data = {
            greeting
        };
        return this.http.patch<any>(this.ENDPOINTS.PUT_PHONESERVICE_URL(phoneserviceId), data, {
            headers: {
                'correlation-id': this.generateUUID(),
                'Authorization': `Bearer${this.token}`
            }
        });
    }

    saveVoiceBotType(voiceBotId: string, phoneserviceId: string) {
        return this.http.post<any>(this.ENDPOINTS.VOICE_BOT_TYPE_URL(voiceBotId, phoneserviceId), {}, {
            headers: {
                'correlation-id': this.generateUUID(),
                'Authorization': `Bearer${this.token}`
            }
        });
    }

    initTestCall(phoneNumber: string, phoneserviceId: string) {
        const data = {
            phoneNumber: phoneNumber
        };
        return this.http.post<any>(this.ENDPOINTS.TEST_CALL_URL(phoneserviceId), data, {
            headers: {
                'correlation-id': this.generateUUID(),
                'Authorization': `Bearer${this.token}`
            }
        });
    }
    
    createEmailNotification$(email: string, userAccountId: number, phoneServiceId: string) {
        const data = {
            urgentOnly: false,
            reachability: {
                userAccountId: userAccountId,
                name: 'voice-bot-email',
                destination: email,
                type: 'EMAIL',
                makePublicOnRequest: false,
                callNotificationEmailFormat: 'HTML'
            }
        };
        return this.http.post<any>(this.ENDPOINTS.NOTIFICATION_URL(phoneServiceId), data, {
            headers: {
                'correlation-id': this.generateUUID(),
                'Authorization': `Bearer${this.token}`
            }
        });
    }

    fetchNotificationTargets$(phoneServiceId: string) {
        return this.http.get<NotificationRes>(this.ENDPOINTS.NOTIFICATION_URL(phoneServiceId), {
            headers: {
                'correlation-id': this.generateUUID(),
                'Authorization': `Bearer${this.token}`,
                'accept': '*/*'
            }
        });
    }

    deleteNotificationTargets(phoneServiceId: string, notificationId: number) {
        this.http.delete<any>(this.ENDPOINTS.DELETE_NOTIFICATION_URL(phoneServiceId, notificationId), {
            headers: {
                'correlation-id': this.generateUUID(),
                'Authorization': `Bearer${this.token}`,
                'accept': '*/*'
            }
        });
    }
    
    createCallScenario(phoneServiceId: string, text: string){
        const data = {
            phoneservice_id: phoneServiceId,
            text: text
        };
        return this.http.post<CallScenario>(this.ENDPOINTS.CALL_SCENARIO_URL(), data, {
            headers: {
                'content-type': 'application/json'
            }
        });
    }
    
    // success - 200?
    deleteCallScenario(phoneServiceId: string, tool_id: number) {
        return this.http.delete<any>(this.ENDPOINTS.DELETE_CALL_SCENARIO(phoneServiceId, tool_id), {
            headers: {
                'correlation-id': this.generateUUID(),
                'Authorization': `Bearer${this.token}`
            }
        });
    }
    
    addCallScenarioProperty(psId: string, callScenario: CallScenario, text: string) {
        const data = {
            phoneservice_id: psId,
            call_scenario: callScenario,
            text: text
        };
        return this.http.post<CallScenario>(this.ENDPOINTS.CALL_SCENARIO_URL(), data, {
            headers: {
                'content-type': 'application/json'
            }
        });
    }

    deleteCallScenarioProperty(psId: string, propertyName: string, callScenario: CallScenario) {
        const data = {
            phoneservice_id: psId,
            deleted_parameter: propertyName,
            call_scenario: callScenario
        };
        return this.http.patch<CallScenario>(this.ENDPOINTS.CALL_SCENARIO_URL(), data, {
            headers: {
                'content-type': 'application/json'
            }
        });
    }
    
    private addInternalHeader(httpHeader: HttpHeaders) {
        const key = environment?.localOrigin;
        const val = environment?.localOriginValue;
        
        return !key ? httpHeader : httpHeader.append(key, val);
    }
    
    private generateUUID(): string {
        let timeInMs = new Date().getTime();
        let timeInMsFromPerformance = ((typeof performance !== 'undefined') && performance.now && (performance.now() * 1000)) || 0;
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (uuidTemplate) => {
            let randomNumber = Math.random() * 16;
            if (timeInMs > 0) {
                // tslint:disable-next-line:no-bitwise
                randomNumber = (timeInMs + randomNumber) % 16 | 0;
                timeInMs = Math.floor(timeInMs / 16);
            } else {
                // tslint:disable-next-line:no-bitwise
                randomNumber = (timeInMsFromPerformance + randomNumber) % 16 | 0;
                timeInMsFromPerformance = Math.floor(timeInMsFromPerformance / 16);
            }
            // tslint:disable-next-line:no-bitwise
            return (uuidTemplate === 'x' ? randomNumber : (randomNumber & 0x3 | 0x8)).toString(16);
        });
    };
    
    
    sendContactRequest(msg: Message, userAccountId: number) {
        const formData = new FormData();
        formData.append('message', new Blob([JSON.stringify(msg)], {
            type: 'application/json'
        }));
        // @ts-ignore
        formData.append('files', null);
        
        return this.http.post<any>(this.ENDPOINTS.CONTACT_REQUEST_URL(userAccountId), formData,{
            headers: {
                'correlation-id': this.generateUUID(),
                'Authorization': `Bearer${this.token}`
            }
        });
    }
}
